import { Avatar } from '../Avatar'
import { ClientEntity } from './ClientEntity'

export class ClientCharacter extends ClientEntity {
  static type = 'character'

  getTargetInfo() {
    return this.data.target
  }

  isHitBy(item) {
    if (this.data.combat?.dead) return false
    if (item?.consumable && !item?.use) return false
    return true
  }

  init() {
    const c = this.data.collider
    this._collider = this.world.physics.add(
      this.data.position.x,
      this.data.position.y,
      { type: 'character', ox: c.ox, oy: c.oy, w: c.w, h: c.h },
      this
    )

    const target = this.data.target || {}
    this._target = this.world.targets.add({
      px: this.data.position.x,
      py: this.data.position.y,
      ox: target.ox,
      oy: target.oy,
      radius: target.radius || 0,
      value: this,
    })

    const unit = this.data.unit
    this._unit = this.world.frames.create({
      type: 'unit',
      wx: this.data.position.x + (unit.ox ?? 0),
      wy: this.data.position.y + unit.oy,
      name: unit.name,
      health: this.data.combat?.health ?? 100,
      maxHealth: this.data.combat?.maxHealth ?? 100,
      energy: this.data.combat?.energy ?? 0,
      showEnergy: this.data.id === this.world.playerId,
    })
    if (this.data.id === this.world.playerId) {
      this.world.emit('resonance', this.data.combat?.resonance ?? 100)
    }

    this._avatar = new Avatar(this.world, this.world.uniforms.uTime, this.world.lights, {
      yBase: this.data.position.yBase,
      width: this.data.avatar.width,
      height: this.data.avatar.height,
      totalFrames: this.data.avatar.totalFrames,
      animations: this.data.avatar.animations,
      shadow: this.data.avatar.shadow,
    })
    this._avatar.setLayers(this.data.avatar.layers)
    this._avatar.setDirection(this.data.avatar.direction)
    this._avatar.setLocomotion(this.data.avatar.locomotion)
    this._avatar.setPosition(this.data.position.x, this.data.position.y)
  }

  onChange(changes) {
    if (changes.position) {
      const unit = this.data.unit
      this._collider?.setPosition(this.data.position.x, this.data.position.y)
      this._target?.setPosition(this.data.position.x, this.data.position.y)
      this._unit?.setPosition(this.data.position.x + (unit.ox ?? 0), this.data.position.y + unit.oy)
      this._avatar?.setPosition(this.data.position.x, this.data.position.y)
      this._chatBubble?.setPosition(this.data.position.x + (unit.ox ?? 0), this.data.position.y + unit.oy)
    }
    if (changes.unit?.name) {
      this._unit?.setProps({ name: this.data.unit.name })
    }
    if (changes.combat?.health !== undefined) {
      this._unit?.setProps({ health: this.data.combat.health })
    }
    if (changes.combat?.energy !== undefined) {
      this._unit?.setProps({ energy: this.data.combat.energy })
    }
    if (changes.combat?.resonance !== undefined && this.data.id === this.world.playerId) {
      this.world.emit('resonance', this.data.combat.resonance)
    }
    if (changes.combat?.dead === false) {
      this._avatar?.playEmote(null)
    }
    if (changes.avatar?.locomotion) {
      this._avatar?.setLocomotion(this.data.avatar.locomotion)
    }
    if (changes.avatar?.direction) {
      this._avatar?.setDirection(this.data.avatar.direction)
    }
    if (changes.avatar?.layers) {
      this._avatar?.setLayers(this.data.avatar.layers)
    }
  }

  // ── public API for controllers ──

  getDirection() {
    return this._avatar?.getDirection?.() ?? this.data.avatar.direction
  }

  getLocomotion() {
    return this._avatar?.getLocomotion?.() ?? this.data.avatar.locomotion
  }

  isEmotePlaying() {
    return this._avatar?.isEmotePlaying() ?? false
  }

  move(dx, dy) {
    this._collider.move(dx, dy)
    const c = this.data.collider
    this.data.position.x = this._collider.x - c.ox
    this.data.position.y = this._collider.y - c.oy
    const unit = this.data.unit
    this._target?.setPosition(this.data.position.x, this.data.position.y)
    this._unit?.setPosition(this.data.position.x + (unit.ox ?? 0), this.data.position.y + unit.oy)
    this._avatar?.setPosition(this.data.position.x, this.data.position.y)
    this._chatBubble?.setPosition(this.data.position.x + (unit.ox ?? 0), this.data.position.y + unit.oy)
  }

  showChatBubble(text) {
    clearTimeout(this._chatBubbleTimer)
    this._chatBubble?.destroy()
    this._chatBubble = null
    const unit = this.data.unit
    this._unit?.setProps({ hidden: true })
    this._chatBubble = this.world.frames.create({
      type: 'bubble',
      wx: this.data.position.x + (unit.ox ?? 0),
      wy: this.data.position.y + unit.oy,
      text,
    })
    this._chatBubbleTimer = setTimeout(() => {
      this._chatBubble?.destroy()
      this._chatBubble = null
      this._unit?.setProps({ hidden: false })
    }, 5000)
  }

  face(direction) {
    this.data.avatar.direction = direction
    this._avatar?.setDirection(direction)
  }

  setLocomotion(locomotion) {
    this.data.avatar.locomotion = locomotion
    this._avatar?.setLocomotion(locomotion)
  }

  playEmote(emote) {
    this._avatar?.playEmote(emote)
  }

  isBlocked(x1, y1, x2, y2, targetEntity) {
    if (!this._ignoreSet) this._ignoreSet = new Set()
    this._ignoreSet.clear()
    this._ignoreSet.add(this._collider)
    if (targetEntity?._collider) this._ignoreSet.add(targetEntity._collider)
    return this.world.physics.raycast(x1, y1, x2, y2, 'solid', this._ignoreSet)
  }

  destroy() {
    clearTimeout(this._chatBubbleTimer)
    this._chatBubble?.destroy()
    this._chatBubble = null
    this._avatar?.destroy()
    this._unit?.destroy()
    this._target?.destroy()
    this._collider?.destroy()
    super.destroy()
  }
}
